import { useEffect, useRef } from 'react'
import { pickAudioContextCtor } from './audioContext'

// Lazy Web Audio graph: AudioContext is created at the FIRST `play()`
// when the effective gain ≠ 1.0. Three reasons this trigger and not
// "on-mount when gain ≠ 1.0":
//   1) RecordingsPage renders up to 50 <AudioPlayer>s simultaneously;
//      mounting all of them with persisted gain=2.5 would spawn 50
//      AudioContexts (browsers cap ~6 per origin in Chrome).
//   2) Constructing on slider change instead would silently NOT apply
//      a persisted boost after refresh — the listener would hear native
//      volume until they touch the slider again. Confusing.
//   3) `play()` from a click handler is itself a user gesture, so the
//      AudioContext starts in `running` state — autoplay policy happy.
//
// After init the graph is the SOLE volume path: createMediaElementSource
// detaches the element from the default audio→speakers routing, so the
// native `audio.volume` no longer affects output. We pin audio.volume=1
// and drive everything through GainNode.

// Exported for reuse — useCallAudioBoost (live calls) wants the same
// broadcast-style compressor params. Single source of truth so tuning
// stays in lockstep across recordings and live audio.
export const COMPRESSOR_PARAMS = {
  threshold: -3,
  ratio: 4,
  attack: 0.003,
  release: 0.05,
}

interface Graph {
  ctx: AudioContext
  source: MediaElementAudioSourceNode
  gain: GainNode
  compressor: DynamicsCompressorNode
  compressorEngaged: boolean
}

/**
 * Wires gain (and optional compressor) into the audio element's playback
 * path. Returns nothing — caller drives it via the gain prop.
 *
 * Lifecycle:
 *   - mount: read audio element, attach `play` listener that builds graph lazily
 *   - gain change: if graph live → update GainNode + (re)wire compressor
 *   - gain change without graph: stays no-op until first play
 *   - unmount: disconnect nodes, close ctx
 *
 * Fallback: if AudioContext is unavailable (jsdom, very old browsers),
 * gain is clamped to [0, 1] and applied via native audio.volume. Boost
 * > 100% is unavailable in this mode.
 */
export function useAudioBoost(audioRef: React.RefObject<HTMLAudioElement | null>, gain: number): void {
  const graphRef = useRef<Graph | null>(null)
  const gainRef = useRef(gain)
  // Tracks whether the user has pressed play at least once. Used to decide
  // whether a slider-driven gain rise should lazily build the graph: yes
  // mid-playback (the bug fix below), no on initial mount with persisted
  // boost (would saturate browser AudioContext pool on RecordingsPage with
  // 50 rows). Set in `play` listener, never cleared.
  const hasPlayedRef = useRef(false)
  // Holds the current build function so the gain-change effect can call
  // it. Captured by the persistent-setup effect (single source of truth
  // for the AudioContext + node graph). Null after unmount cleanup.
  const buildGraphRef = useRef<(() => Graph | null) | null>(null)

  // Keep gainRef in sync without touching it during render — the
  // play-handler effect captures gainRef.current at fire-time, so we
  // need the latest value reachable from inside the listener.
  useEffect(() => {
    gainRef.current = gain
  }, [gain])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const ctxCtor = pickAudioContextCtor()

    // Returns the live graph (existing or newly built) and writes it
    // into graphRef. Centralised here so both onPlay and the gain-change
    // effect can request a build without duplicating the assignment —
    // the gain-change effect must NOT touch graphRef directly because
    // react-hooks/immutability flags writing a ref value inside an
    // effect that later reads through it.
    const ensureGraph = (): Graph | null => {
      if (graphRef.current) return graphRef.current
      if (!ctxCtor) return null
      try {
        const ctx = new ctxCtor()
        const source = ctx.createMediaElementSource(audio)
        const gainNode = ctx.createGain()
        gainNode.gain.value = gainRef.current
        const compressor = ctx.createDynamicsCompressor()
        compressor.threshold.value = COMPRESSOR_PARAMS.threshold
        compressor.ratio.value = COMPRESSOR_PARAMS.ratio
        compressor.attack.value = COMPRESSOR_PARAMS.attack
        compressor.release.value = COMPRESSOR_PARAMS.release

        source.connect(gainNode)
        const engageCompressor = gainRef.current > 1
        if (engageCompressor) {
          gainNode.connect(compressor)
          compressor.connect(ctx.destination)
        } else {
          gainNode.connect(ctx.destination)
        }

        // Once the graph rules, native volume must stay at 1 — the
        // element is detached from default routing anyway, but pinning
        // it keeps any stray `<audio volume>` attribute mutations from
        // confusing future readers.
        audio.volume = 1
        const g: Graph = { ctx, source, gain: gainNode, compressor, compressorEngaged: engageCompressor }
        graphRef.current = g
        ctx.resume().catch(() => {})
        return g
      } catch {
        // createMediaElementSource throws if called twice on the same
        // element across different contexts. Shouldn't happen given our
        // singleton-per-Player invariant, but bail safely if it does.
        return null
      }
    }

    buildGraphRef.current = ensureGraph

    const onPlay = (): void => {
      hasPlayedRef.current = true
      if (graphRef.current) {
        // already built — make sure ctx is running (resume after suspend)
        graphRef.current.ctx.resume().catch(() => {})
        return
      }
      if (gainRef.current === 1) return // no boost requested, skip init
      ensureGraph()
    }

    audio.addEventListener('play', onPlay)

    return () => {
      audio.removeEventListener('play', onPlay)
      buildGraphRef.current = null
      const g = graphRef.current
      graphRef.current = null
      if (g) {
        try { g.source.disconnect() } catch { /* ignore */ }
        try { g.gain.disconnect() } catch { /* ignore */ }
        try { g.compressor.disconnect() } catch { /* ignore */ }
        try { g.ctx.close() } catch { /* ignore */ }
      }
    }
  }, [audioRef])

  // Apply gain changes. Three paths:
  //   - graph live → drive GainNode (and re-wire compressor if needed)
  //   - graph absent BUT user is mid-playback and raised boost → build now.
  //     Without this branch a user who pressed Play at gain=1 and then
  //     dragged the slider to 300% would hear no change: the play-time
  //     early-return skipped graph init, and the fallback path below
  //     clamps gain to [0,1] so audio.volume stays 1.
  //   - graph absent + no user gesture yet (initial mount with persisted
  //     gain) → fallback via audio.volume (clamped 0..1). hasPlayedRef
  //     gates the lazy build so a /staff/recordings page with 50 rows
  //     doesn't spawn 50 AudioContexts on mount.
  useEffect(() => {
    if (!graphRef.current && gain > 1 && hasPlayedRef.current && buildGraphRef.current) {
      // ensureGraph writes graphRef.current as a side-effect; we re-read
      // below rather than capturing the return so this effect doesn't
      // alias a graphRef-derived `let` binding (react-hooks/immutability).
      buildGraphRef.current()
    }
    const g = graphRef.current
    if (g) {
      g.gain.gain.value = gain
      const wantCompressor = gain > 1
      if (wantCompressor !== g.compressorEngaged) {
        try { g.gain.disconnect() } catch { /* ignore */ }
        if (wantCompressor) {
          g.gain.connect(g.compressor)
          // compressor → destination is already connected from build
          // step OR from prior boost cycle. To be safe, ensure it.
          try { g.compressor.disconnect() } catch { /* ignore */ }
          g.compressor.connect(g.ctx.destination)
        } else {
          try { g.compressor.disconnect() } catch { /* ignore */ }
          g.gain.connect(g.ctx.destination)
        }
        g.compressorEngaged = wantCompressor
      }
      return
    }
    // Fallback path: no AudioContext available (jsdom or unsupported).
    // Clamp gain to [0, 1] via native volume. Boost > 100% impossible here.
    const audio = audioRef.current
    if (!audio) return
    const v = Math.max(0, Math.min(1, gain))
    try { audio.volume = v } catch { /* ignore */ }
  }, [gain, audioRef])
}
